import React from "react";
import {
  Box,
  Typography,
  Grid,
  Link,
  useTheme,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { StandardPageHead } from "../components/Heads/StandardPageHead.tsx";
import { cityLandingPageInfo } from "../data/fixedCityLocations.ts";

export const CitiesOverviewPage: React.FC = () => {
  const theme = useTheme();

  const [expandedCities, setExpandedCities] = React.useState<Set<string>>(
    () => new Set(),
  );

  const toggleCity = React.useCallback((cityName: string) => {
    setExpandedCities((prev) => {
      const next = new Set(prev);
      if (next.has(cityName)) next.delete(cityName);
      else next.add(cityName);
      return next;
    });
  }, []);

  return (
    <>
      <StandardPageHead
        title="All showtimes of every film showing near you"
        description="View All Popular Cities With Listings on CinemaGuide. Quickly compare showtimes and buy cinema tickets."
        pageUrl="https://cinemaguideireland.com/popular-cities"
      />
      <Typography
        variant="h1"
        component="h1"
        gutterBottom
        sx={{
          marginLeft: { xs: 0.5, md: 2 },
          marginRight: { xs: 0.5, md: 2 },
          paddingTop: { xs: 1, md: 1 },
          marginTop: 6,
          mb: { xs: 6, md: 10 },
          fontSize: { xs: "1.5rem", md: "2rem" },
          fontWeight: 500,
          textDecoration: "underline",
          textAlign: "center",
        }}
      >
        Popular Cities Overview
      </Typography>

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          mx: "auto",
          width: { md: "80%", xs: "95%" },
          mt: { xs: 2, md: 4 },
          mb: 2,
          gap: 2,
        }}
      >
        {cityLandingPageInfo.map(({ locationName, slug: citySlug }) => {
          return (
            <Accordion
              key={citySlug}
              disableGutters
              elevation={0}
              expanded={expandedCities.has(citySlug)}
              onChange={() => toggleCity(citySlug)}
              sx={{
                width: "100%",
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 2,
                "&:before": { display: "none" },
                overflow: "hidden",
                fontSize: { xs: "1.2rem", md: "1.6rem" },
              }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography
                  variant="h4"
                  sx={{
                    textAlign: "left",
                    fontSize: { xs: "1.1rem", md: "1.6rem" },
                    fontWeight: 500,
                  }}
                >
                  {locationName}
                </Typography>
              </AccordionSummary>

              <AccordionDetails sx={{ mt: 2 }}>
                <Grid container spacing={{ xs: 1.5, sm: 2, md: 4 }}>
                  {[
                    { dateSlug: "", label: "on All Dates" },
                    { dateSlug: "/today", label: "Today" },
                    { dateSlug: "/tomorrow", label: "Tomorrow" },
                  ].map(({ dateSlug, label }) => (
                    <Grid
                      key={`${citySlug}${dateSlug}`}
                      item
                      xs={12}
                      sm={6}
                      sx={{ display: "flex", alignItems: "center" }}
                    >
                      <Link
                        href={`/${citySlug}${dateSlug}`}
                        color={theme.palette.primary.main}
                        sx={{
                          mb: 2,
                          fontSize: { xs: "large", md: "x-large" },
                          "&:hover": { cursor: "pointer" },
                          textShadow: "0 0 4px rgba(0,160,255,.4)",
                        }}
                      >
                        {`Films showing ${label}`}
                      </Link>
                    </Grid>
                  ))}
                </Grid>
              </AccordionDetails>
            </Accordion>
          );
        })}
      </Box>
    </>
  );
};
